import {switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as getterHelper from "../helpers/getterHelper";
import * as handlers from "../constants/handlers";
import * as methods from "../constants/methods";
import * as settings from "../constants/settings";

const formulaStoreInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.FORMULA_STORE_INIT),
        switchMap(({payload}) => {
            if (state$.value.searchPanel[payload.tabId] !== undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.SEARCH_PANEL_INIT,
                    payload: {
                        tabId: payload.tabId,
                        type: payload.type,
                        local: false
                    }
                },
                {
                    type: actionTypes.COLORANT_PANEL_INIT,
                    payload: {
                        tabId: payload.tabId,
                        type: payload.type
                    }
                }
            ];
        })
    );

const formulaStoreAutoInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MENU_ADD_NEW_OR_OPEN_MAIN_TAB),
        switchMap(() => {
            if (state$.value.formula[state$.value.navigation.activeTab] !== undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.FORMULA_STORE_INIT,
                    payload: {
                        tabId: state$.value.navigation.activeTab,
                        type: componentTypes.MAIN_TAB
                    }
                }
            ];
        })
    );

const formulaColorPaletteStoreAutoInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MENU_ADD_NEW_OR_OPEN_COLOR_PALETTE_TAB),
        switchMap(() => {
            if (state$.value.formula[state$.value.navigation.activeTab] !== undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.FORMULA_STORE_INIT,
                    payload: {
                        tabId: state$.value.navigation.activeTab,
                        type: componentTypes.COLOR_TAB
                    }
                }
            ];
        })
    );

const formulaColorSearchStoreAutoInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MENU_ADD_NEW_OR_OPEN_COLOR_SEARCH_TAB),
        switchMap(() => {
            if (state$.value.formula[state$.value.navigation.activeTab] !== undefined) {
                return [];
            }

            return [
                {
                    type: actionTypes.FORMULA_STORE_INIT,
                    payload: {
                        tabId: state$.value.navigation.activeTab,
                        type: componentTypes.COLOR_SEARCH_TAB
                    }
                }
            ];
        })
    );

const editFormulaColorants = (action$, state$) =>
    action$.pipe(
        ofType(
            actionTypes.FORMULA_COLORANTS_EDIT,
            actionTypes.MAIN_TAB_SAVE_FORMULA_COLORANTS,
            actionTypes.MAIN_TAB_SAVE_MODIFIED_FORMULA_COLORANTS
        ),
        switchMap(({payload}) => {
            if (
                state$.value.formula[payload.tabId] === undefined ||
                state$.value.formula[payload.tabId].formula === null
            ) {
                return [];
            }

            return [
                {
                    type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                    payload: {
                        tabId: payload.tabId,
                        formula: state$.value.formula[payload.tabId].formula.getColorantVolumes(),
                        numCanDone: state$.value.formula[payload.tabId].numCanDone,
                        numCanTotal: state$.value.formula[payload.tabId].numCanTotal,
                        customCan: state$.value.formula[payload.tabId].package?.isCustom() ?? false
                    }
                }
            ];
        })
    );

const validateFormulas = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_MACHINE_CANISTERS),
        switchMap(() => {
            let actions = [];
            let tabs = state$.value.navigation.tabList.filter(
                (tab) =>
                    tab.type === componentTypes.MAIN_TAB ||
                    tab.type === componentTypes.CUSTOM_TAB ||
                    tab.type === componentTypes.MATCHING_TAB
            );

            let tab;
            for (tab of tabs) {
                if (
                    state$.value.formula[tab.id] !== undefined &&
                    state$.value.formula[tab.id].formula !== undefined &&
                    state$.value.formula[tab.id].formula !== null
                ) {
                    actions.push({
                        type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                        payload: {
                            tabId: tab.id,
                            formula: state$.value.formula[tab.id].formula.getColorantVolumes(),
                            numCanDone: state$.value.formula[tab.id].numCanDone,
                            numCanTotal: state$.value.formula[tab.id].numCanTotal,
                            customCan: state$.value.formula[tab.id].package?.isCustom() ?? false
                        }
                    });
                }
            }

            return actions;
        })
    );

const reloadFormulaPriceData = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.MAIN_TAB_RELOAD_FORMULA_PRICE_DATA),
        switchMap(() => {
            let formulaTabs = state$.value.navigation.tabList.filter(
                (tab) => tab.type === componentTypes.MAIN_TAB || tab.type === componentTypes.CUSTOM_TAB
            );

            let actions = [];

            let formulaStore;
            let tab;
            for (tab of formulaTabs) {
                formulaStore = state$.value.formula[tab.id];

                if (tab.type === componentTypes.CUSTOM_TAB) {
                    actions.push({
                        type: actionTypes.RELOAD_FORMULA_META_DATA,
                        payload: {
                            tabId: tab.id
                        }
                    });

                    continue;
                }

                if (
                    formulaStore === undefined ||
                    formulaStore.product === null ||
                    formulaStore.fandeck === null ||
                    formulaStore.color === null ||
                    formulaStore.formula === null ||
                    formulaStore.package === null
                ) {
                    continue;
                }

                actions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_FORMULA_META_AND_PRINT_DATA,
                        params: {
                            brand: state$.value.server.activeDatabase.name,
                            colorId: formulaStore.color.id,
                            colorLocal: formulaStore.color.local,
                            fandeckId: formulaStore.fandeck.id,
                            fandeckLocal: formulaStore.color.local,
                            productId: formulaStore.product.id,
                            baseId: formulaStore.base === null ? null : formulaStore.base.id,
                            formulaColorants: formulaStore.formula.getRealColorantAmounts(),
                            formulaCorrections: formulaStore.formula.isCorrection()
                                ? formulaStore.formula.getCorrectionAmounts()
                                : null,
                            packageId: formulaStore.package.package.isCustom() ? null : formulaStore.package.id,
                            nominalAmount: formulaStore.package.package.nominalAmount,
                            fillVolume: formulaStore.package.package.filling,
                            unitId: formulaStore.package.package.unit,
                            packageCount: formulaStore.numCanTotal,
                            customPackage: formulaStore.package.package.isCustom(),
                            customPackageSize: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageUnitId: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            marginId: formulaStore.margin === null ? null : formulaStore.margin.id,
                            marginLocal: formulaStore.margin === null ? null : formulaStore.margin.local,
                            customerId: formulaStore.customer === null ? null : formulaStore.customer.id,
                            formulaId: formulaStore.formula.id,
                            formulaLocal: formulaStore.color.local,
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base === null ? null : formulaStore.base.name,
                            fandeckName: formulaStore.fandeck.name,
                            colorName: formulaStore.color.name,
                            packageName: formulaStore.package.package.name,
                            colorantUfiCodes: getterHelper.getFormulaUfiCodes(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.settings.ufiCodeLogic
                            ),
                            baseInProductUfiCode: null,
                            colorantBatchNumbers: getterHelper.getFormulaColorantBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList
                            ),
                            canisterBatchNumbers: getterHelper.getFormulaCanisterBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.machine.allBatchNumbersList
                            )
                        }
                    },
                    method: handlers.GET_FORMULA_META_AND_PRINT_DATA,
                    tabId: tab.id
                });
            }

            return actions;
        })
    );

const loadPackageImage = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FORMULA_DATA),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];
            if (
                formulaStore === undefined ||
                formulaStore.product === null ||
                formulaStore.fandeck === null ||
                formulaStore.color === null ||
                formulaStore.formula === null ||
                formulaStore.package === null
            ) {
                return [];
            }

            let actions = [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_FORMULA_META_AND_PRINT_DATA,
                        params: {
                            brand: state$.value.server.activeDatabase.name,
                            colorId: formulaStore.color.id,
                            colorLocal: formulaStore.color.local,
                            fandeckId: formulaStore.fandeck.id,
                            fandeckLocal: formulaStore.color.local,
                            productId: formulaStore.product.id,
                            baseId: formulaStore.base === null ? null : formulaStore.base.id,
                            formulaColorants: formulaStore.formula.getRealColorantAmounts(),
                            formulaCorrections: formulaStore.formula.isCorrection()
                                ? formulaStore.formula.getCorrectionAmounts()
                                : null,
                            packageId: formulaStore.package.package.isCustom() ? null : formulaStore.package.id,
                            nominalAmount: formulaStore.package.package.nominalAmount,
                            fillVolume: formulaStore.package.package.filling,
                            unitId: formulaStore.package.package.unit,
                            packageCount: formulaStore.numCanTotal,
                            customPackage: formulaStore.package.package.isCustom(),
                            customPackageSize: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageUnitId: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            marginId: formulaStore.margin === null ? null : formulaStore.margin.id,
                            marginLocal: formulaStore.margin === null ? null : formulaStore.margin.local,
                            customerId: formulaStore.customer === null ? null : formulaStore.customer.id,
                            formulaId: formulaStore.formula.id,
                            formulaLocal: formulaStore.color.local,
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base === null ? null : formulaStore.base.name,
                            fandeckName: formulaStore.fandeck.name,
                            colorName: formulaStore.color.name,
                            packageName: formulaStore.package.package.name,
                            colorantUfiCodes: getterHelper.getFormulaUfiCodes(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.settings.ufiCodeLogic
                            ),
                            baseInProductUfiCode: null,
                            colorantBatchNumbers: getterHelper.getFormulaColorantBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList
                            ),
                            canisterBatchNumbers: getterHelper.getFormulaCanisterBatchNumbers(
                                formulaStore.formula.colorantList,
                                state$.value.rcc.canisterList,
                                state$.value.machine.allBatchNumbersList
                            )
                        }
                    },
                    method: payload.navigation
                        ? handlers.NAVIGATION_GET_FORMULA_META_AND_PRINT_DATA
                        : handlers.GET_FORMULA_META_AND_PRINT_DATA,
                    tabId: payload.tabId
                }
            ];

            if (
                state$.value.settings.colorPreviewAsImage &&
                window.imageStore.getCifImageById(formulaStore.fandeck.id, formulaStore.color.id) === null
            ) {
                actions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_COLOR_IMAGE,
                        params: {
                            fandeckId: formulaStore.fandeck.id,
                            colorId: formulaStore.color.id,
                            local: formulaStore.color.local
                        }
                    },
                    method: handlers.GET_COLOR_IMAGE,
                    tabId: payload.tabId,
                    next: {
                        fandeckId: formulaStore.fandeck.id,
                        colorId: formulaStore.color.id
                    }
                });
            }

            if (formulaStore.base !== null) {
                actions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_PRODUCT_IMAGE,
                        params: {
                            productId: formulaStore.product.id || null,
                            baseId: formulaStore.base.id || null,
                            packageId: formulaStore.package.package.id || null,
                            productName: formulaStore.product.name,
                            baseName: formulaStore.base.name,
                            packageName: formulaStore.package.package.name,
                            online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                        }
                    },
                    method: handlers.GET_PRODUCT_IMAGE,
                    tabId: payload.tabId
                });
            } else {
                actions.push({
                    type: actionTypes.MAIN_TAB_SAVE_PACKAGE_IMAGE,
                    payload: {
                        tabId: payload.tabId,
                        imageId: -1
                    }
                });
            }

            if (Array.isArray(formulaStore.moreInfoData) && formulaStore.moreInfoData.length > 0) {
                for (let moreInfo of formulaStore.moreInfoData) {
                    if (moreInfo.hasImage && window.imageStore.getFormulaNoteImageById(moreInfo.id) === null) {
                        actions.push({
                            type: actionTypes.SERVER_REQUEST,
                            payload: {
                                type: methods.METHOD_GET_FORMULA_NOTE_IMAGE,
                                params: {
                                    formulaNoteId: moreInfo.id,
                                    online: state$.value.settings.imagesSource === settings.SOURCE_ONLINE
                                }
                            },
                            method: handlers.GET_FORMULA_NOTE_IMAGE,
                            tabId: componentTypes.NO_SKIP_TAB_ID
                        });
                    }
                }
            }

            return actions;
        })
    );

const loadCustomPriceList = (action$) =>
    action$.pipe(
        ofType(
            actionTypes.CUSTOM_TAB_SELECT_PRODUCT,
            actionTypes.CUSTOM_TAB_SELECT_FANDECK,
            actionTypes.CUSTOM_TAB_SELECT_BASE,
            actionTypes.CUSTOM_TAB_SET_PACKAGE,
            actionTypes.FORMULA_COLORANTS_EDIT,
            actionTypes.CUSTOM_TAB_SAVE_PACKAGE_LIST
        ),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                }
            ];
        })
    );

const updateFormulaInfo = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.UPDATE_DISPENSING_INFO),
        switchMap(({payload}) => {
            if (state$.value.rcc.dispensingFormula === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.UPDATE_FORMULA_DISPENSING_INFO,
                    payload: payload
                }
            ];
        })
    );

const colorantPanelInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.COLORANT_PANEL_INIT),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SET_COLORANT_PANEL_ACTIVE_UNIT,
                    payload: {
                        tabId: payload.tabId,
                        activeUnit: getterHelper.getValidActiveUnit(
                            state$.value.settings.lastUsedUnit,
                            state$.value.settings.visibleUnits,
                            payload.type,
                            state$.value.rcc.machine.typeName
                        )
                    }
                }
            ];
        })
    );

export default [
    formulaStoreInit,
    validateFormulas,
    reloadFormulaPriceData,
    loadPackageImage,
    loadCustomPriceList,
    updateFormulaInfo,
    formulaStoreAutoInit,
    formulaColorPaletteStoreAutoInit,
    formulaColorSearchStoreAutoInit,
    editFormulaColorants,
    colorantPanelInit
];
